import { Button, Grid, TextField, Typography } from "@mui/material";
import Config from "../../Utils/Config";
import { Controller, useForm } from "react-hook-form";
import { FgoSettingsValidationSchema } from "../../Utils/Models";
import { yupResolver } from "@hookform/resolvers/yup";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Save } from "@mui/icons-material";
import { endpoints } from "../../Utils/UrlEnum";
import { updateData } from "../../Services/updateData";
import { AxiosResponse } from "axios";
import { WebSocketContext } from "../../Context/WebSocketContext";
import { useContext, useEffect, useState } from "react";
import styles from "../../Styles/settings.module.css";
import { getData } from "../../Services/getData";
import { GreenSwitch } from "../../Utils/Utils";

export default function FgoIntegration() {
  const websocketContext = useContext(WebSocketContext);
  const [isOblioActive, setIsOblioActive] = useState(false);

  const { control, reset, handleSubmit, watch } = useForm({
    resolver: yupResolver(FgoSettingsValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    criteriaMode: "firstError",
  });

  const isActive = watch("value.isActive");

  useEffect(() => {
    getData(endpoints.setting.fgoSettings).then((res: AxiosResponse) => {
      if (res?.data) {
        reset(res.data);
      }
    });
    // Check Oblio status
    getData(endpoints.setting.oblioSettings).then((res: AxiosResponse) => {
      if (res?.data?.value?.isActive) {
        setIsOblioActive(true);
      }
    });
  }, [reset]);

  useEffect(() => {
    // Listen for Oblio activation to disable FGO
    const handleOblioActivation = () => {
      getData(endpoints.setting.oblioSettings).then((res: AxiosResponse) => {
        if (res?.data?.value?.isActive) {
          setIsOblioActive(true);
        } else {
          setIsOblioActive(false);
        }
      });
      getData(endpoints.setting.fgoSettings).then((res: AxiosResponse) => {
        if (res?.data) {
          reset(res.data);
        }
      });
    };

    if (websocketContext.shouldUpdate) {
      handleOblioActivation();
    }
  }, [websocketContext.shouldUpdate, reset]);

  const onSubmit = (data: any) => {
    const url = `${endpoints.setting.fgo}${data?._id}`;
    updateData(url, data).then((res: AxiosResponse) => {
      if (res?.data) {
        websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
        reset(res.data);
      }
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.settingsForm}>
      {/* Active Toggle */}
      <Grid container spacing={Config.standardGridSpacing} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Typography
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {Vocabulary.fgoIsActive}
            <Controller
              name="value.isActive"
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <GreenSwitch
                  checked={value}
                  onChange={onChange}
                  disabled={isOblioActive}
                />
              )}
            />
            {isOblioActive && (
              <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                {Vocabulary.disableOblioToActivateFgo}
              </Typography>
            )}
          </Typography>
        </Grid>
      </Grid>

      {/* Input fields */}
      {isActive && !isOblioActive && (
        <Grid container spacing={Config.standardGridSpacing} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Controller
              name="value.url"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={Vocabulary.fgoApiUrl}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  placeholder="https://example.com"
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="value.pass"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={Vocabulary.fgoPass}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      )}

      {/* Save Button */}
      <Grid container spacing={Config.standardGridSpacing}>
        <Grid item xs={12} md={3}>
          <Button
            type="submit"
            fullWidth
            startIcon={<Save />}
            variant="contained"
          >
            {Vocabulary.save}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
